import { useEffect, useState } from "react";
import { api, API, authFetch } from "@/lib/api";
import { useLocations } from "@/context/LocationContext";
import { Download, FileText, FileArchive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel } from "@/components/ui-bits";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function Reports() {
  const { selected, params } = useLocations();
  const [batches, setBatches] = useState([]);
  const [q, setQ] = useState("");
  const [bundleDays, setBundleDays] = useState("30");
  const [bundleBusy, setBundleBusy] = useState(false);

  useEffect(() => {
    api.get("/batches", { params: params() }).then((r) => setBatches(r.data));
    // params() depends on `selected`; re-fetching on selected change is the
    // intentional behaviour, no need for `params` reference equality here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const downloadCsv = (code) => {
    authFetch(`${API}/reports/batch/${code}.csv`)
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `batch_${code}.csv`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const downloadPdf = async (code) => {
    const { data } = await api.get(`/trace/batch/${code}`);
    const b = data.batch;
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Batch Traceability Report", 14, 16);
    doc.setFontSize(11);
    doc.text(`Batch: ${b.batch_code}    Recipe: ${b.recipe_name}`, 14, 26);
    doc.text(`Stage: ${b.stage}    Planned: ${b.quantity_planned}    Produced: ${b.quantity_produced || "—"}`, 14, 33);
    doc.text(`Started: ${b.started_at?.slice(0,16).replace("T"," ")}    Finished: ${b.finished_at?.slice(0,16).replace("T"," ") || "—"}`, 14, 40);

    autoTable(doc, {
      startY: 48,
      head: [["Ingredients (backward trace)", "Supplier", "Supplier batch", "Internal code", "Qty"]],
      body: (b.consumptions || []).map((c) => [c.material_name, c.supplier_name, c.supplier_batch, c.internal_batch_code, `${c.quantity} ${c.unit}`]),
    });
    autoTable(doc, {
      head: [["Packing (forward)", "Pack size", "Packs"]],
      body: data.packing.map((p) => [p.label_code, `${p.pack_size} ${p.pack_unit}`, p.packs_produced]),
    });
    autoTable(doc, {
      head: [["Dispatches (forward)", "Customer", "Date", "Label", "Packs"]],
      body: data.dispatches.flatMap((d) => (d.items || []).map((it) => [d.dispatch_code, d.customer_name, d.dispatch_date?.slice(0,10), it.label_code, it.quantity_packs])),
    });
    doc.save(`batch_${code}.pdf`);
  };

  const filtered = batches.filter((b) =>
    !q ||
    b.batch_code?.toLowerCase().includes(q.toLowerCase()) ||
    b.recipe_name?.toLowerCase().includes(q.toLowerCase())
  );

  const downloadBundle = async () => {
    setBundleBusy(true);
    try {
      const qs = new URLSearchParams({ days: bundleDays, ...(selected ? { location_id: selected } : {}) });
      const r = await authFetch(`${API}/reports/audit-bundle.zip?${qs.toString()}`);
      if (!r.ok) throw new Error("Failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-bundle-${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit bundle downloaded");
    } catch (e) {
      toast.error("Could not generate bundle");
    } finally {
      setBundleBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Reports" eyebrow="Quality · Batch & recall reports" />

      <Panel
        title="SALSA / BRCGS audit bundle"
        action={
          <Button
            onClick={downloadBundle}
            disabled={bundleBusy}
            className="h-11 gap-2"
            data-testid="download-audit-bundle"
          >
            <FileArchive className="h-4 w-4" />
            {bundleBusy ? "Preparing…" : "Download .zip"}
          </Button>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-bold mb-1.5">
              Window
            </div>
            <Select value={bundleDays} onValueChange={setBundleDays}>
              <SelectTrigger className="h-12" data-testid="bundle-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 text-sm text-stone-600 leading-relaxed">
            Generates a ready-to-share ZIP containing CSV exports of goods intake, production
            batches, per-batch traceability reports, packing, dispatches, waste, cleaning logs
            and any recorded recall drills — filtered to the currently selected location.
          </div>
        </div>
      </Panel>

      <Panel title="Generate a batch report">
        <div className="mb-4">
          <Input className="h-12" placeholder="Filter by batch code or recipe…" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="report-filter" />
        </div>
        {filtered.length === 0 ? (
          <div className="py-10 text-center text-stone-500">No batches.</div>
        ) : (
          <table className="w-full" data-testid="reports-table">
            <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
              <tr><th className="py-3">Batch</th><th>Recipe</th><th>Stage</th><th className="text-right">Produced</th><th className="text-right">Export</th></tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-t border-border h-16">
                  <td className="font-mono text-sm">{b.batch_code}</td>
                  <td>{b.recipe_name}</td>
                  <td className="text-stone-600">{b.stage}</td>
                  <td className="text-right tnum">{b.quantity_produced || "—"}</td>
                  <td className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={()=>downloadCsv(b.batch_code)} data-testid={`csv-${b.id}`}>
                      <Download className="h-4 w-4 mr-1" /> CSV
                    </Button>
                    <Button size="sm" onClick={()=>downloadPdf(b.batch_code)} data-testid={`pdf-${b.id}`}>
                      <FileText className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
